/**
 * Iteratively-rendered recipe tree (no recursive JSX — see comment
 * below). Flattens the tree into a list of {node, depth, hasChildren,
 * pathKey} rows, lets the user toggle each branch by depth-prefixed
 * key.
 *
 * We avoid `<PlannerTreeNode>` calling itself because the
 * `@emergentbase/visual-edits` babel plugin overflows its call stack
 * when statically analyzing self-referential JSX — extracting to a
 * separate file isn't enough, so we hand-walk the tree instead.
 */
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { resolveImg as resolveIcon } from "@/lib/resolveImg";

function MatImg({ image, name, size = 22 }) {
  const src = resolveIcon(image);
  if (!src) {
    return (
      <div
        className="rounded bg-white/5 border border-white/10 flex items-center justify-center text-[10px] text-[var(--muted)] shrink-0"
        style={{ width: size, height: size }}
        title={name}
      >?</div>
    );
  }
  return (
    <img
      src={src} alt={name} title={name} width={size} height={size}
      className="rounded shrink-0 object-contain bg-black/30 ring-1 ring-white/10 p-0.5"
      onError={(e) => { e.target.style.display = "none"; }}
      loading="lazy"
    />
  );
}

function buildRows(root, openSet, defaultOpen) {
  const rows = [];
  // DFS stack — each frame: {node, depth, key}
  const stack = [{ node: root, depth: 0, key: "0:" + root.name }];
  while (stack.length > 0) {
    const { node, depth, key } = stack.pop();
    const hasChildren = (node.children || []).length > 0 && node.kind === "craft";
    const isOpen = openSet.has(key) ?? false;
    const effectiveOpen = openSet.size === 0 ? depth < defaultOpen : isOpen;
    rows.push({ node, depth, key, hasChildren, open: effectiveOpen });
    if (hasChildren && effectiveOpen) {
      // Push children in reverse so DFS pops them in original order
      const kids = node.children;
      for (let i = kids.length - 1; i >= 0; i--) {
        const child = kids[i];
        stack.push({
          node: child,
          depth: depth + 1,
          key: `${depth + 1}:${key}>${child.name}-${i}`,
        });
      }
    }
  }
  return rows;
}

export default function PlannerTreeNode({ node, defaultOpen = 2 }) {
  // Explicitly toggled rows. Empty set = use defaultOpen heuristic.
  const [openSet, setOpenSet] = useState(new Set());

  const rows = useMemo(
    () => buildRows(node, openSet, defaultOpen),
    [node, openSet, defaultOpen]
  );

  const toggle = (key, currentlyOpen) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      // First explicit toggle: seed with everything currently visible-open
      if (next.size === 0) {
        rows.forEach((r) => { if (r.open && r.hasChildren) next.add(r.key); });
      }
      if (currentlyOpen) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div className="text-sm" data-testid="planner-tree-rows">
      {rows.map((r) => {
        const kindBadge = r.node.kind === "raw" ? null
          : r.node.kind === "cycle" ? <span className="text-[9px] text-red-300 ml-1">cycle</span>
          : r.node.kind === "max-depth" ? <span className="text-[9px] text-red-300 ml-1">max</span>
          : null;
        return (
          <div
            key={r.key}
            className="flex items-center gap-2 py-1"
            style={{ paddingLeft: `${r.depth * 18}px` }}
            data-testid={`planner-tree-node-${r.node.name}`}
          >
            <button
              type="button"
              onClick={() => r.hasChildren && toggle(r.key, r.open)}
              className={`w-4 shrink-0 ${r.hasChildren ? "cursor-pointer" : "cursor-default"}`}
              aria-label={r.hasChildren ? (r.open ? "Collapse" : "Expand") : undefined}
            >
              {r.hasChildren && (r.open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
            </button>
            <MatImg image={r.node.image} name={r.node.name} size={22} />
            <span className="text-[var(--bone)]">
              <span className="text-[var(--teal)] font-semibold tabular-nums">
                {r.node.qty.toLocaleString()}
              </span>
              {" × "}{r.node.name}{kindBadge}
            </span>
          </div>
        );
      })}
    </div>
  );
}
